<template>
  <div class="mall-inquiry-write">
    <header class="mall-inquiry-write__head">
      <h1>문의하기</h1>
      <p>주문번호를 첨부해 문의를 작성합니다. 상품 문의와 그 밖 문의 모두 여기서 남깁니다.</p>
    </header>

    <div v-if="created" class="mall-inquiry-write__done">
      <p class="mall-inquiry-write__done-label">등록 완료</p>
      <h2>문의가 등록되었습니다</h2>
      <dl class="mall-inquiry-write__done-info">
        <div>
          <dt>글 번호</dt>
          <dd>{{ created.id }}</dd>
        </div>
      </dl>
      <p class="mall-inquiry-write__done-note">
        답변이 등록되면 내가 작성한 문의에서 확인할 수 있습니다.
      </p>
      <div class="mall-inquiry-write__done-actions">
        <router-link :to="`${MALL_MY_CONSULTS_PATH}/${created.id}`" class="mall-inquiry-write__btn mall-inquiry-write__btn--solid">
          문의 내용 보기
        </router-link>
        <router-link :to="MALL_MY_CONSULTS_PATH" class="mall-inquiry-write__btn">문의 내역</router-link>
      </div>
    </div>

    <form v-else class="mall-inquiry-write__panel" @submit.prevent="onSubmit">
      <div class="mall-inquiry-write__body">
        <div class="mall-inquiry-write__field">
          <span>주문번호 <i>*</i></span>
          <select v-model="form.orderCode" required :disabled="ordersLoading || !orders.length">
            <option value="" disabled>주문번호를 선택해 주세요</option>
            <option v-for="order in orders" :key="order.code" :value="order.code">
              {{ formatOrderOption(order) }}
            </option>
          </select>
          <p v-if="ordersLoading" class="mall-inquiry-write__hint">주문 목록을 불러오는 중…</p>
          <p v-else-if="!orders.length" class="mall-inquiry-write__hint">
            주문내역이 없어 문의를 작성할 수 없습니다.
          </p>
          <p v-else class="mall-inquiry-write__hint">
            문의에는 내 주문번호가 첨부됩니다.
          </p>

          <div v-if="selectedOrder" class="mall-inquiry-write__order">
            <div class="mall-inquiry-write__order-head">
              <strong>{{ selectedOrder.code }}</strong>
              <em class="mall-inquiry-write__order-status">{{ mallBuyerOrderStatusLabel(selectedOrder) }}</em>
            </div>
            <dl class="mall-inquiry-write__order-meta">
              <div>
                <dt>주문일시</dt>
                <dd>{{ formatDateTime(selectedOrder.orderedAt) }}</dd>
              </div>
              <div>
                <dt>결제금액</dt>
                <dd>{{ formatPrice(selectedOrder.totalAmount) }}원</dd>
              </div>
            </dl>
            <p class="mall-inquiry-write__order-items">
              {{ selectedOrder.itemSummary || '주문 상품' }}
              <span v-if="selectedOrder.itemCount > 1">외 {{ selectedOrder.itemCount - 1 }}건</span>
            </p>
            <router-link
              :to="`/mall/orders/${encodeURIComponent(selectedOrder.code)}`"
              class="mall-inquiry-write__order-link"
            >
              주문 상세 보기
            </router-link>
          </div>
        </div>

        <label class="mall-inquiry-write__field">
          <span>제목 <i>*</i></span>
          <input v-model="form.title" type="text" required maxlength="50" placeholder="문의 제목" />
        </label>

        <div class="mall-inquiry-write__row">
          <label class="mall-inquiry-write__field">
            <span>기관명 <i>*</i></span>
            <input v-model="form.orgName" type="text" required maxlength="20" />
          </label>
          <label class="mall-inquiry-write__field">
            <span>기관 기호 <i>*</i></span>
            <input v-model="form.orgCode" type="text" required maxlength="11" />
          </label>
        </div>

        <div class="mall-inquiry-write__row">
          <label class="mall-inquiry-write__field">
            <span>연락처 <i>*</i></span>
            <input
              v-model="form.contactPhone"
              type="tel"
              required
              inputmode="numeric"
              maxlength="13"
              placeholder="01012345678"
              autocomplete="tel"
              @blur="onContactPhoneBlur"
            />
          </label>
          <label class="mall-inquiry-write__field">
            <span>이메일 주소</span>
            <input
              v-model="form.email"
              type="email"
              maxlength="50"
              placeholder="example@email.com"
              autocomplete="email"
            />
          </label>
        </div>

        <label class="mall-inquiry-write__field">
          <span>상담 내용 <i>*</i></span>
          <textarea
            v-model="form.content"
            rows="8"
            required
            maxlength="5000"
            placeholder="문의 내용을 입력해 주세요."
          />
        </label>

        <p v-if="error" class="mall-inquiry-write__error">{{ error }}</p>
      </div>

      <div class="mall-inquiry-write__foot">
        <router-link :to="MALL_MY_CONSULTS_PATH" class="mall-inquiry-write__btn">취소</router-link>
        <button
          type="submit"
          class="mall-inquiry-write__btn mall-inquiry-write__btn--solid"
          :disabled="submitting || ordersLoading || !form.orderCode"
        >
          {{ submitting ? '등록 중...' : '문의 등록' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { createConsultPost } from '@/api/consultBoard'
import { fetchMyOrders, mallBuyerOrderStatusLabel, type MallOrder } from '@/api/mallOrder'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { formatKrPhone } from '@/utils/formatPhone'
import { formatPrice } from '@/constants/mallProducts'
import { MALL_MY_CONSULTS_PATH } from '@/constants/mallMy'

const route = useRoute()
const { member } = useMemberAuth()

const form = reactive({
  orderCode: String(route.query.order || '').trim(),
  title: '',
  orgName: member.value?.orgName || '',
  orgCode: member.value?.longtermCode || '',
  contactPhone: member.value?.bossPhone || member.value?.orgPhone || '',
  email: member.value?.bossEmail || '',
  content: '',
})

const orders = ref<MallOrder[]>([])
const ordersLoading = ref(false)

/* 고른 주문이 어떤 건인지 바로 아래 카드로 보여 준다 */
const selectedOrder = computed(
  () => orders.value.find((row) => row.code === form.orderCode) || null,
)

const submitting = ref(false)
const error = ref('')
const created = ref<{ id: number; authorId: string } | null>(null)

const onContactPhoneBlur = () => {
  form.contactPhone = formatKrPhone(form.contactPhone)
}

function formatOrderOption(order: MallOrder) {
  const when = String(order.orderedAt || '').slice(0, 10)
  const summary = String(order.itemSummary || '').trim()
  return [order.code, when, summary].filter(Boolean).join(' · ')
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

const loadOrders = async () => {
  ordersLoading.value = true
  try {
    orders.value = await fetchMyOrders()
    const fromQuery = String(route.query.order || '').trim()
    if (fromQuery && orders.value.some((row) => row.code === fromQuery)) {
      form.orderCode = fromQuery
    } else if (form.orderCode && !orders.value.some((row) => row.code === form.orderCode)) {
      form.orderCode = ''
    }
  } catch {
    orders.value = []
  } finally {
    ordersLoading.value = false
  }
}

const onSubmit = async () => {
  error.value = ''
  if (!form.orderCode) {
    error.value = '주문번호를 첨부해 주세요.'
    return
  }

  const formData = new FormData()
  formData.append('channel', 'MALL')
  formData.append('orderCode', form.orderCode)
  formData.append('title', form.title.trim())
  formData.append('orgName', form.orgName.trim())
  formData.append('orgCode', form.orgCode.trim())
  formData.append('contactPhone', formatKrPhone(form.contactPhone))
  formData.append('email', form.email.trim())
  formData.append('content', form.content.trim())

  submitting.value = true
  try {
    created.value = await createConsultPost(formData)
  } catch (e) {
    error.value = e instanceof Error ? e.message : '등록에 실패했습니다.'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  void loadOrders()
})
</script>

<style lang="scss" src="./MallInquiryWritePage.scss"></style>
